<!-- src/components/MainLayout.vue -->
<template>
  <div class="container main-layout">
    <!-- Navigation Row -->
    <div class="row align-items-center">
      <div class="col-sm text-start">
        <img
          class="img-responsive header-logo"
          src="../assets/golfnow.png"
          alt="GolfNow logo image"
        />
      </div>
      <div class="col-sm"></div>
      <div class="col-sm text-end">
        <button
          class="dp-tab"
          :class="{ active: page === 0 }"
          @click="setPage(0)"
        >
          <i class="bi bi-calendar-event me-2"></i>Seasons
        </button>
        <button
          class="dp-tab"
          :class="{ active: page === 1 }"
          @click="setPage(1)"
        >
          <i class="bi bi-wrench ms-3"></i>Strategies
        </button>
        <button class="dp-tab logout-btn" @click="logout">
          <i class="bi bi-box-arrow-right me-2"></i>Logout
        </button>
      </div>
    </div>

    <div class="row dropdowns-row align-items-center mb-3">
      <div class="col-md-8 d-flex flex-nowrap align-items-center">
        <!-- Facility Section -->
        <div
          class="facility-dropdown d-flex align-items-center me-3 flex-shrink-0"
        >
          <label class="dd-label me-2">Facility:</label>
          <div class="dropdown">
            <button
              class="btn btn-primary-outline dropdown-toggle"
              type="button"
              id="facilityDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {{ facilityName }}
            </button>
            <ul class="dropdown-menu" aria-labelledby="facilityDropdown">
              <li v-for="f in facilities" :key="f.id">
                <a
                  class="dropdown-item"
                  href="#"
                  @click="f.id !== undefined ? fetchCourses(f.id) : null"
                  @mouseleave="facilityName = f.name ?? ''"
                >
                  {{ f.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Course Section -->
        <div class="course-dropdown d-flex align-items-center flex-shrink-0">
          <label class="dd-label me-2">Course:</label>
          <div class="dropdown">
            <button
              class="btn btn-primary-outline dropdown-toggle"
              type="button"
              id="courseDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {{ courseName }}
            </button>
            <ul class="dropdown-menu" aria-labelledby="courseDropdown">
              <li v-for="c in courses" :key="c.id">
                <a
                  class="dropdown-item"
                  href="#"
                  @click="c.id !== undefined ? fetchSeasons(c.id) : null"
                  @mouseleave="courseName = c.name ?? ''"
                >
                  {{ c.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Add Button Section -->
      <div class="col-md-4 text-end">
        <button
          type="button"
          class="btn add-season-btn"
          :class="{ 'disabled-btn': !selectedCourseId, 'd-none': page !== 0 }"
          :disabled="!selectedCourseId"
          data-bs-toggle="modal"
          data-bs-target="#createSeasonModal"
        >
          <i class="bi bi-calendar-plus"></i>
        </button>
        <button
          type="button"
          class="btn add-strategy-btn"
          :class="{ 'd-none': page !== 1 }"
          data-bs-toggle="modal"
          data-bs-target="#createStrategyModal"
        >
          <i class="bi bi-plus-circle"></i>
        </button>
        <button
          v-if="
            (!selectedStrategy?.isMultipleWindows &&
              pricingWindows.length === 0) ||
            selectedStrategy?.isMultipleWindows
          "
          title="Add Pricing Window"
          type="button"
          class="btn add-strategy-btn"
          :class="{ 'd-none': page !== 2 }"
          @click.prevent="addPriceWindow"
        >
          <i class="bi bi-plus-circle"></i>
        </button>
      </div>
    </div>

    <div class="page-content">
      <h3 class="page-title text-center">
        <i class="bi bi-calendar-event me-2"></i>{{ currentPageTitle }}
      </h3>
      <div v-if="page === 0">
        <!-- Current Seasons -->
        <div v-if="seasons.length > 0">
          <div v-for="s in currentSeasons" :key="s.id">
            <SeasonComponent
              :season="s"
              :strategies="strategies"
              :courseId="selectedCourseId ?? 0"
              :existingSeasons="seasons"
              @season-updated="onSeasonUpdated"
              @season-deleted="onSeasonDeleted"
              @season-delete-error="onSeasonDeleteError"
              @season-copied="onSeasonCopied"
              @season-save-error="onSeasonSaveError"
            />
          </div>
          <!-- Similarly for previousSeasons -->
        </div>

        <!-- Previous Seasons -->
        <div v-if="previousSeasons.length > 0" class="previous-seasons-section">
          <button
            class="btn btn-secondary w-100 text-start"
            @click="togglePreviousSeasons"
            :class="{ expanded: isPreviousSeasonsExpanded }"
          >
            <i
              :class="[
                'bi',
                isPreviousSeasonsExpanded ? 'bi-chevron-up' : 'bi-chevron-down',
              ]"
            >
            </i>
            Previous Seasons
          </button>
          <div
            v-if="isPreviousSeasonsExpanded"
            class="previous-seasons-content"
          >
            <div v-for="s in previousSeasons" :key="s.id">
              <SeasonComponent
                :season="s"
                :strategies="strategies"
                :courseId="selectedCourseId ?? 0"
                :existingSeasons="seasons"
                @season-updated="onSeasonUpdated"
                @season-deleted="onSeasonDeleted"
                @season-delete-error="onSeasonDeleteError"
                @season-save-error="onSeasonSaveError"
                @season-copied="onSeasonCopied"
              />
            </div>
          </div>
        </div>

        <div
          v-if="currentSeasons.length === 0 && previousSeasons.length === 0"
          class="no-seasons-message"
        >
          No seasons found for this course. Click the "+" button to add a new
          season.
        </div>
      </div>
      <div v-if="page === 1">
        <div v-for="strategy in strategies" :key="strategy.id">
          <StrategyComponent
            :strategy="strategy"
            :facilities="facilities"
            :existingStrategies="strategies"
            @strategy-selected="onStrategySelected"
          />
        </div>
      </div>
      <div v-if="page === 2">
        <PricingComponent
          :strategy="selectedStrategy ?? {}"
          :pricingWindows="pricingWindows"
          @season-updated="onPricingSeasonUpdated"
        />
      </div>
    </div>
  </div>
  <CreateSeasonModal
    v-if="selectedCourseId !== null"
    :courseId="selectedCourseId"
    :existingSeasons="seasons"
    @season-created="onSeasonCreated"
  />
  <CreateStrategyModal
    v-if="selectedFacilityId !== null"
    :facilityId="selectedFacilityId"
    :existingStrategies="strategies"
    @strategy-created="onStrategyCreated"
    @strategy-selected="onStrategySelected"
  />
  <div class="wrapper"></div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { jwtDecode } from 'jwt-decode';
import SeasonComponent from './SeasonComponent.vue';
import CreateStrategyModal from './CreateStrategyModal.vue';
import { Season, Facility, Course, Strategy, PriceWindow } from '../api';
import StrategyComponent from './StrategyComponent.vue';
import CreateSeasonModal from './SeasonsCreateComponent.vue';
import PricingComponent from './PricingComponent.vue';
import eventBus, { EventType } from '../eventBus';
import {
  seasonsApi,
  facilityApi,
  courseApi,
  strategyApi,
  pricingApi,
} from '../apiClient';
import { AuthService } from '../services/auth-service';
import { useToast } from 'vue-toastification';

export default defineComponent({
  name: 'MainLayout',
  components: {
    SeasonComponent,
    StrategyComponent,
    PricingComponent,
    CreateSeasonModal,
    CreateStrategyModal,
  },
  setup() {
    const route = useRoute();
    const seasons = ref<Season[]>([]);
    const facilities = ref<Facility[]>([]);
    const courses = ref<Course[]>([]);
    const strategies = ref<Strategy[]>([]);
    let selectedStrategy = ref<Strategy>();
    const pricingWindows = ref<PriceWindow[]>([]);
    const page = ref(0);

    const facilityName = ref('Select Facility');
    const courseName = ref('Select Course');
    const selectedCourseId = ref<number | null>(null);
    const selectedFacilityId = ref<number | null>(null);

    function getInitialFacilityAndCourse(): {
      facilityId: number | null;
      courseId: number | null;
    } {
      if (route.query.facilityId && route.query.courseId) {
        return {
          facilityId: Number(route.query.facilityId),
          courseId: Number(route.query.courseId),
        };
      }

      const storedFacilityId = localStorage.getItem('dp_facilityId');
      const storedCourseId = localStorage.getItem('dp_courseId');
      if (storedFacilityId && storedCourseId) {
        return {
          facilityId: Number(storedFacilityId),
          courseId: Number(storedCourseId),
        };
      }

      const jwt = localStorage.getItem('jwt');
      if (jwt) {
        try {
          const decoded = jwtDecode<{ facilityId?: number; courseId?: number }>(
            jwt
          );
          if (decoded.facilityId && decoded.courseId) {
            return {
              facilityId: Number(decoded.facilityId),
              courseId: Number(decoded.courseId),
            };
          }
        } catch (e) {
          console.error('Error decoding JWT:', e);
        }
      }

      return { facilityId: null, courseId: null };
    }

    const pageTitles = [
      'Season Management',
      'Strategy Management',
      'Pricing Logic',
    ];

    const toast = useToast();

    const isPreviousSeasonsExpanded = ref(false);

    const currentDate = new Date();

    const seasonsComputed = computed(() => {
      return seasons.value.reduce(
        (acc, season) => {
          if (season.endDate && !isNaN(new Date(season.endDate).getTime())) {
            if (new Date(season.endDate) >= currentDate) {
              acc.currentSeasons.push(season);
            } else {
              acc.previousSeasons.push(season);
            }
          } else {
            acc.currentSeasons.push(season);
          }
          return acc;
        },
        { currentSeasons: [] as Season[], previousSeasons: [] as Season[] }
      );
    });

    const currentSeasons = computed(() => seasonsComputed.value.currentSeasons);
    const previousSeasons = computed(
      () => seasonsComputed.value.previousSeasons
    );

    const currentPageTitle = computed(() => {
      return pageTitles[page.value] || 'Dashboard';
    });

    const togglePreviousSeasons = () => {
      isPreviousSeasonsExpanded.value = !isPreviousSeasonsExpanded.value;
    };

    const onSeasonCreated = () => {
      refreshSeasons();
      toast.success('Season created successfully!');
    };

    const onStrategyCreated = () => {
      fetchStrategies();
      toast.success('Strategy created successfully!');
    };

    const onSeasonUpdated = () => {
      refreshSeasons();
      toast.success('Season updated successfully!');
    };

    const onSeasonDeleted = () => {
      refreshSeasons();
      toast.info('Season deleted successfully!');
    };

    const onSeasonCopied = () => {
      refreshSeasons();
      toast.success(`Season copied successfully!`);
    };

    const onSeasonSaveError = (errorMessage: string) => {
      toast.error(errorMessage);
    };

    const onPricingSeasonUpdated = () => {
      refreshSeasons();
      toast.success('Pricing updated successfully!');
    };

    const onSeasonDeleteError = (errorMessage: string) => {
      toast.error(errorMessage);
    };

    const refreshSeasons = () => {
      if (selectedCourseId.value) {
        fetchSeasons(selectedCourseId.value);
      }
    };

    const fetchSeasons = async (courseId: number) => {
      try {
        selectedCourseId.value = courseId;
        const response = await seasonsApi.seasonGet(courseId);
        seasons.value = response.data;

        if (seasons.value && seasons.value.length > 0) {
          seasons.value.sort((a, b) => {
            if (a.startDate && b.startDate) {
              return a.startDate < b.startDate ? -1 : 1;
            }
            return 0;
          });
        }
      } catch (error) {
        console.error('Error fetching seasons:', error);
        toast.error('Failed to load seasons. Please try again.');
      }
    };

    const fetchFacilities = async (initialFacilityId?: number | null) => {
      try {
        const groupId = AuthService.getSecurityGroup();
        const response = await facilityApi.facilitiesGet(groupId);
        facilities.value = response.data;

        if (initialFacilityId && facilities.value.length > 0) {
          const facility = facilities.value.find(
            (facility) => facility.id === initialFacilityId
          );
          if (facility) {
            selectedFacilityId.value = initialFacilityId;
            facilityName.value = facility.name || 'Select Facility';
            return;
          }
        }

        // select first facility
        if (facilities.value.length && facilities.value[0]?.id) {
          fetchCourses(facilities.value[0].id);
          facilityName.value = facilities.value[0].name || '';
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };

    const fetchCourses = async (
      facilityId: number,
      initialCourseId?: number | null
    ) => {
      try {
        selectedFacilityId.value = facilityId;
        const response = await courseApi.coursesGet(facilityId);
        courses.value = response.data;
        seasons.value = [];
        selectedCourseId.value = null;

        if (initialCourseId && courses.value.length > 0) {
          const course = courses.value.find(
            (course) => course.id === initialCourseId
          );
          if (course) {
            selectedCourseId.value = initialCourseId;
            courseName.value = course.name || 'Select Course';
            await fetchStrategies();
            await fetchSeasons(initialCourseId);
            return;
          }
        }

        // select first course
        if (courses.value.length && courses.value[0]?.id) {
          await fetchStrategies();
          fetchSeasons(courses.value[0].id);
          courseName.value = courses.value[0].name || '';
          selectedCourseId.value = courses.value[0].id;
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    const fetchStrategies = async () => {
      try {
        const id = selectedFacilityId.value ?? undefined;
        const response = await strategyApi.apiStrategyGet(id);
        strategies.value = response.data;
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };

    const fetchPricingWindows = async () => {
      try {
        if (!selectedStrategy.value) {
          return;
        }
        const response = await pricingApi.pricingGet(selectedStrategy.value.id);
        pricingWindows.value = response.data;
      } catch (error) {
        console.error('Error fetching pricing windows:', error);
      }
    };

    const onStrategySelected = (strategy: Strategy) => {
      selectedStrategy.value = strategy;
      fetchPricingWindows();
    };

    const logout = () => {
      AuthService.logout();

      if (window.parent !== window) {
        window.parent.postMessage('closeDynamicPricing', '*');
      }
    };

    const setPage = (p: number) => {
      page.value = p;
    };

    const addPriceWindow = () => {
      eventBus.emit(EventType.CreateNewPriceWindow);
    };

    onMounted(async () => {
      const { facilityId: initialFacilityId, courseId: initialCourseId } =
        getInitialFacilityAndCourse();

      await fetchFacilities(initialFacilityId);

      if (initialFacilityId) {
        await fetchCourses(initialFacilityId, initialCourseId);
      }

      if (localStorage.getItem('dp_facilityId')) {
        localStorage.removeItem('dp_facilityId');
      }
      if (localStorage.getItem('dp_courseId')) {
        localStorage.removeItem('dp_courseId');
      }

      eventBus.on(EventType.ShowPricingLogic, async (data) => {
        if (!data || !data.strategyId) {
          return;
        }
        const found =
          data.strategyObj ??
          strategies.value.find((s) => s.id === data.strategyId);
        if (!found) {
          return;
        }

        selectedStrategy.value = found;
        setPage(2);
      });

      eventBus.on(EventType.RefreshStrategies, () => {
        fetchStrategies();
      });
    });

    return {
      seasons,
      facilities,
      courses,
      strategies,
      page,
      setPage,
      logout,
      fetchCourses,
      fetchSeasons,
      refreshSeasons,
      onSeasonDeleted,
      onSeasonCopied,
      onPricingSeasonUpdated,
      currentPageTitle,
      facilityName,
      courseName,
      selectedFacilityId,
      selectedCourseId,
      onSeasonCreated,
      onSeasonUpdated,
      onSeasonDeleteError,
      onSeasonSaveError,
      currentSeasons,
      previousSeasons,
      isPreviousSeasonsExpanded,
      togglePreviousSeasons,
      selectedStrategy,
      pricingWindows,
      onStrategySelected,
      onStrategyCreated,
      addPriceWindow,
    };
  },
});
</script>

<style scoped>
.wrapper {
  background-image: url(../assets/background.jpg);
  border-image: fill 0 linear-gradient(#f9f9f9, #f9f9f9, #f9f9f9e2, #f9f9f924);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  z-index: -1;
}

.main-layout {
  padding: 20px;
  background-color: #f9f9f9 !important;
}

.page-content {
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: block;
  min-height: 75vh;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 1px 5px rgba(0, 0, 0, 0.12);
  padding: 20px;
  border-radius: 8px;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #405f6b;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title i {
  font-size: 1.2rem;
  margin-right: 0.5rem;
}

img.header-logo {
  display: inline-block;
  max-height: 125px;
  height: auto;
  width: auto;
  margin: 0px auto;
  vertical-align: middle;
}

.add-btn {
  font-size: 1.5rem;
  font-weight: bolder;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 1px 5px rgba(0, 0, 0, 0.09);
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 8px;
  transition: transform 0.2s ease, background-color 0.3s ease;
}

.add-btn:hover {
  transform: scale(1.1);
  background-color: #e6f2f2;
}

.dd-label {
  font-weight: bold;
  color: #405f6b;
  margin-right: 5px;
  white-space: nowrap;
}

.dropdowns-row {
  margin-top: 20px;
}

.facility-dropdown,
.course-dropdown {
  display: flex;
  align-items: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.dropdown .btn {
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: white;
  padding: 8px 12px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  white-space: nowrap;
  margin-right: 10px;
}

.dropdown .btn:hover,
.dropdown .btn:focus {
  background-color: #f1f1f1;
  border-color: #618183;
}

.dropdown-menu {
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.dropdown-item:hover {
  background-color: #618183;
  color: white;
}

.dp-tab {
  letter-spacing: 0.09rem;
  background-color: transparent;
  border: none;
  border-radius: 5px;
  margin: 5px;
  padding: 5px 10px;
  text-transform: uppercase;
  font-weight: 700;
  transition: background-color 0.3s, color 0.3s;
  color: #618183;
}

.dp-tab:hover {
  background-color: #e6f2f2;
  color: #405f6b;
}

.dp-tab.active {
  background-color: #405f6b;
  color: #ffffff;
}

.logout-btn {
  color: #dc3545;
}

.logout-btn:hover {
  background-color: #dc3545;
  color: white;
}

.add-season-btn {
  background-color: #405f6b;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  width: 50px;
  height: 50px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.add-season-btn i {
  font-size: 1.2rem;
  position: relative;
  z-index: 2;
  color: #ffffff;
}

.add-season-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #007bff;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  z-index: 1;
}

.add-season-btn:hover::before {
  transform: translateY(0);
}

.add-season-btn:hover i {
  color: #ffffff;
}

.add-season-btn:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.add-season-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.5);
}

.add-season-btn.disabled-btn {
  background-color: #cccccc;
  color: #666666;
  cursor: not-allowed;
}

.add-season-btn.disabled-btn::before {
  display: none;
}

.add-season-btn.disabled-btn:hover {
  box-shadow: none;
  transform: none;
}

@media (max-width: 768px) {
  .dropdowns-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .facility-dropdown,
  .course-dropdown {
    margin-bottom: 10px;
  }
}

@media (max-width: 576px) {
  .dd-label {
    font-size: 0.9rem;
  }

  .add-season-btn {
    width: 40px;
    height: 40px;
    margin-bottom: 10px;
  }

  .add-season-btn i {
    font-size: 1rem;
  }
}

.previous-seasons-section {
  margin-top: 20px;
}

.previous-seasons-section .btn {
  background-color: #f8f9fa;
  border-color: #ddd;
  color: #405f6b;
  transition: all 0.3s ease;
  font-weight: bold;
}

.previous-seasons-section .btn:hover,
.previous-seasons-section .btn[aria-expanded='true'] {
  background-color: #405f6b;
  color: #ffffff;
}

.previous-seasons-section .btn i {
  margin-right: 10px;
  transition: transform 0.3s ease;
}

.previous-seasons-section .btn[aria-expanded='true'] i {
  transform: rotate(180deg);
}

.previous-seasons-content {
  margin-top: 10px;
  padding: 15px;
  background-color: #ffffff;
  border-radius: 5px;
  border: 1px solid #ddd;
  transition: all 0.3s ease;
}

.previous-seasons-section.expanded .previous-seasons-content {
  background-color: #405f6b;
  color: #ffffff;
}

.add-strategy-btn {
  background-color: #405f6b;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  width: 50px;
  height: 50px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.add-strategy-btn i {
  font-size: 1.2rem;
  position: relative;
  z-index: 2;
  color: #ffffff;
}

.add-strategy-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #28a745;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  z-index: 1;
}

.add-strategy-btn:hover::before {
  transform: translateY(0);
}

.add-strategy-btn:hover i {
  color: #ffffff;
}

.add-strategy-btn:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.add-strategy-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.5);
}
</style>
